import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { PrismicRichText } from '@prismicio/react';
import PrismicLink from '../../../PrismicLink/PrismicLink';
import css from './FeatureRow.module.scss';
import cn from 'classnames';
import { StaticImage, GatsbyImage } from 'gatsby-plugin-image';
import { isFilled } from '@prismicio/helpers';
import { m } from 'framer-motion';

const FaqItem = ({ data, active, setActive }) => {
  if (isFilled.keyText(data.name)) {
    return (
      <div className={css.featuresWrapper}>
        <dt>
          <button className={css.feature} onClick={() => setActive(!active)}>
            <span aria-hidden="true" className={css.openClose}>
              {active ? '-' : '+'}
            </span>
            <h4 className={css.featureTitle}>{data.name}</h4>
          </button>
        </dt>

        <m.dd
          initial="collapsed"
          animate={active ? 'open' : 'collapsed'}
          exit="collapsed"
          variants={{
            open: { opacity: 1, height: 'auto', visibility: 'visible' },
            collapsed: { opacity: 0, height: 0, visibility: 'hidden' },
          }}
          transition={{ duration: 0.7, ease: [0.04, 0.62, 0.23, 0.98] }}
          className={css.textVisible}
        >
          <PrismicRichText field={data.description.richText} />
        </m.dd>
      </div>
    );
  }
};

const FeatureRow = ({ slice, slices, index }) => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (slice.items.length > 0) {
      setActive(slice.items[0]);
    }
  }, [slice.items]);

  const isFirstInGroup = slices[index - 1]?.slice_type !== 'feature_row';
  const isLastInGroup = slices[index + 1]?.slice_type !== 'feature_row';

  return (
    <section id={slice.primary.anchor_id} className={cn('FeatureRow-module', css.container)}>
      <div
        className={cn(css.grid, {
          [css.image_is_right]: slice.primary.desktop_image_is_right,
          [css.rounded_top]: isFirstInGroup,
          [css.rounded_bottom]: isLastInGroup,
        })}
      >
        <div className={css.image}>
          <StaticImage
            className={css.grainyGradient}
            objectFit="cover"
            src="../../../../../static/grainygradient.png"
            alt=""
          />
          {/* Multiple images for expandable cards */}
          {slice.items.length > 0 &&
            isFilled.image(active?.image) &&
            slice.items.map(item => {
              return (
                <div
                  className={cn(css.imageWrapper, {
                    [css.isHidden]: item.image.url !== active.image.url,
                  })}
                >
                  <GatsbyImage
                    key={item.image.url}
                    className={css.gatsby_image}
                    objectFit="contain"
                    image={item.image.gatsbyImageData}
                    alt={item.image.alt || ''}
                  />
                </div>
              );
            })}
          {/* Single image */}
          {slice.items.length === 0 && !isFilled.image(active?.image) ? (
            <GatsbyImage
              objectFit="contain"
              image={slice.primary.image.gatsbyImageData}
              alt={slice.primary.image.alt || ''}
            />
          ) : null}
        </div>
        <div className={css.text}>
          <div>
            {isFilled.richText(slice.primary.eyebrowRich?.richText) ||
            isFilled.richText(slice.primary.eyebrow?.richText) ? (
              <div className={css.eyebrow}>
                <PrismicRichText
                  field={slice.primary.eyebrowRich?.richText || slice.primary.eyebrow?.richText}
                />
              </div>
            ) : null}

            <div className={css.title}>
              <PrismicRichText field={slice.primary.title.richText} />
            </div>

            {slice.primary.item_type !== 'link' && slice.items.length > 0 ? (
              <dl className={css.featureList}>
                {slice.items.map((item, idx) => {
                  return (
                    <FaqItem
                      setActive={() => setActive(item)}
                      active={active === item}
                      key={idx}
                      data={item}
                    />
                  );
                })}
              </dl>
            ) : null}
          </div>
          <div>
            {slice.primary.item_type === 'link' && slice.items.length > 0 ? (
              <div
                className={cn(css.linkGroup, {
                  [css.rightAlign]: slice.primary.desktop_image_is_right,
                })}
              >
                {slice.items.map((item, idx) => {
                  return (
                    <div key={idx} className={css.link}>
                      <PrismicLink to={item.link?.url}>
                        {!slice.primary.desktop_image_is_right ? (
                          <span className={css.leftArrow}>
                            <svg width="15" height="9.374" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="m14.725 5.35-3.75 3.75a.937.937 0 1 1-1.326-1.326L11.8 5.625H.937A.943.943 0 0 1 0 4.661c0-.545.42-.91.938-.91H11.8L9.65 1.6A.937.937 0 1 1 10.977.274l3.75 3.75a.938.938 0 0 1-.002 1.325Z"
                                fill="#FF5257"
                                fill-rule="nonzero"
                              />
                            </svg>
                          </span>
                        ) : null}

                        <span>{item.name}</span>
                        {slice.primary.desktop_image_is_right ? (
                          <span className={css.rightArrow}>
                            <svg width="15" height="9.374" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="m14.725 5.35-3.75 3.75a.937.937 0 1 1-1.326-1.326L11.8 5.625H.937A.943.943 0 0 1 0 4.661c0-.545.42-.91.938-.91H11.8L9.65 1.6A.937.937 0 1 1 10.977.274l3.75 3.75a.938.938 0 0 1-.002 1.325Z"
                                fill="#FF5257"
                                fill-rule="nonzero"
                              />
                            </svg>
                          </span>
                        ) : null}
                      </PrismicLink>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {isFilled.keyText(slice.primary.cta_text) ? (
              <div className={css.link}>
                <PrismicLink to={slice.primary.cta_link?.url}>
                  <span>{slice.primary.cta_text}</span>
                  <span className={css.rightArrow}>
                    <svg width="15" height="9.374" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="m14.725 5.35-3.75 3.75a.937.937 0 1 1-1.326-1.326L11.8 5.625H.937A.943.943 0 0 1 0 4.661c0-.545.42-.91.938-.91H11.8L9.65 1.6A.937.937 0 1 1 10.977.274l3.75 3.75a.938.938 0 0 1-.002 1.325Z"
                        fill="#FF5257"
                        fill-rule="nonzero"
                      />
                    </svg>
                  </span>{' '}
                </PrismicLink>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment MarketplaceTypePageDataBodyFeatureRow on PrismicMarketplaceTypePageDataBodyFeatureRow {
    primary {
      eyebrowRich: eyebrow {
        richText
      }
      anchor_id
      desktop_image_is_right
      cta_text
      cta_link {
        url
      }
      image {
        gatsbyImageData(placeholder: NONE)
        alt
      }
      title {
        richText
      }
    }
    items {
      image {
        gatsbyImageData(placeholder: NONE)
        url
        alt
      }
      name
      description {
        richText
      }
    }
  }
  fragment FeatureHighlightPageDataBodyFeatureRow on PrismicFeatureHighlightPageDataBodyFeatureRow {
    primary {
      eyebrowRich: eyebrow {
        richText
      }
      anchor_id
      desktop_image_is_right
      cta_text
      cta_link {
        url
      }
      image {
        url
        gatsbyImageData(placeholder: NONE)
        alt
      }
      title {
        richText
      }
    }
    items {
      image {
        gatsbyImageData(placeholder: NONE)
        url
        alt
      }
      name
      description {
        richText
      }
    }
  }
  fragment HomepageDataBodyFeatureRow on PrismicHomepageDataBodyFeatureRow {
    primary {
      item_type
      eyebrowRich: eyebrow {
        richText
      }
      desktop_image_is_right
      cta_text
      cta_link {
        url
      }
      image {
        url
        gatsbyImageData(placeholder: NONE)
        alt
      }
      title {
        richText
      }
    }
    items {
      image {
        gatsbyImageData(placeholder: NONE)
        url
        alt
      }
      name
      description {
        richText
      }
      link {
        url
      }
    }
  }
  fragment LandingPageDataBodyFeatureRow on PrismicLandingPageDataBodyFeatureRow {
    primary {
      item_type
      eyebrowRich: eyebrow {
        richText
      }
      desktop_image_is_right
      cta_text
      cta_link {
        url
      }
      image {
        url
        gatsbyImageData(placeholder: NONE)
        alt
      }
      title {
        richText
      }
    }
    items {
      image {
        gatsbyImageData(placeholder: NONE)
        url
        alt
      }
      name
      description {
        richText
      }
      link {
        url
      }
    }
  }
`;

export default FeatureRow;
